import type {
  AxiosError,
  AxiosInstance,
  InternalAxiosRequestConfig,
} from 'axios';
import { sleep } from '@/core/lib';

type FailedQueueItem = {
  resolve: (param: any) => any;
  reject: (error: any) => any;
};
// for multiple requests
let isRefreshing = false;
const failedQueue: FailedQueueItem[] = [];
const instances: AxiosInstance[] = [];

const queueRequest = async (
  _axios: AxiosInstance,
  originalRequest: RetryableRequest,
  isFailedRequest: boolean,
) => {
  try {
    const token = await new Promise(function (resolve, reject) {
      failedQueue.push({ resolve, reject });
    });
    originalRequest.headers['Authorization'] = 'Bearer ' + token;
    if (isFailedRequest) {
      return _axios.request(originalRequest);
    }
    return originalRequest;
  } catch (err) {
    return Promise.reject(err);
  }
};
export const clearQueue = () => {
  failedQueue.length = 0;
};
const processQueue = (error: any, token: string | null = null) => {
  for (const prom of failedQueue) {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  }

  failedQueue.length = 0;
};
type RetryableRequest = InternalAxiosRequestConfig & {
  _retry: boolean;
};

let accessToken: null | string = null;
let accessTokenExpiresAt = 0;
const lockName = 'reco__auth_token_refresh_lock';
const getLock = () => {
  const staleLockAge = 2000;
  const lock = parseInt(localStorage.getItem(lockName) ?? '0', 10);
  if (Date.now() - lock > staleLockAge) {
    localStorage.setItem(lockName, Date.now().toString());
    return true;
  }
  return false;
};
const clearLock = () => {
  localStorage.removeItem(lockName);
};

export type FetchAccessTokenFn = (_axios: AxiosInstance) => Promise<{
  access_token: string;
  expires_in: number;
}>;
const updateToken = async (
  _axios: AxiosInstance,
  authTokenUpdated: AuthTokenUpdatedFn,
  fetchAccessToken: FetchAccessTokenFn,
): Promise<void> => {
  let count = 0;
  while (!getLock() && count++ < 10) {
    await sleep(1000);
  }

  if (Date.now() < accessTokenExpiresAt) {
    processQueue(null, accessToken ?? '');
    return;
  }
  isRefreshing = true;

  try {
    const data = await fetchAccessToken(_axios);

    setAccessToken({
      access_token: data.access_token,
      expires_in: data.expires_in,
    })

    processQueue(null, data.access_token);

    if (authTokenUpdated) {
      authTokenUpdated(data.access_token, data.expires_in * 1000);
    }
  } catch (err) {
    accessTokenExpiresAt = 0;
    accessToken = null;
    //processQueue(err, null);
    throw err;
  } finally {
    isRefreshing = false;
    clearLock();
  }
};

const toRetriableRequest = (
  origRequest: InternalAxiosRequestConfig,
): RetryableRequest => {
  if ('_retry' in origRequest) {
    return origRequest as RetryableRequest;
  }
  return {
    ...origRequest,
    _retry: false,
  };
};

export type AuthTokenUpdatedFn = (token: string, expiresIn: number) => void;

export const setAccessToken = (token: {
  access_token: string;
  expires_in: number;
}) => {
  accessToken = token.access_token;
  accessTokenExpiresAt = Date.now() + token.expires_in * 1000;

  for (const axiosInstance of instances) {
    axiosInstance.defaults.headers.common['Authorization'] =
      'Bearer ' + accessToken;
  }
};

const createInterceptor = (
  axiosInstance: AxiosInstance,
  authTokenUpdated: AuthTokenUpdatedFn,
  onRefreshFailed: () => void,
  fetchAccessToken: FetchAccessTokenFn,
  refreshUrl: string,
): {
  response: (error: any) => Promise<any>;
  request: (config: InternalAxiosRequestConfig) => Promise<any>;
} => {
  const _axios = axiosInstance;
  instances.push(_axios);

  return {
    response: async (error: AxiosError) => {
      const origRequest = error.config;

      if (!origRequest) {
        return Promise.reject(error);
      }

      if (!error.response) {
        // network error
        return Promise.reject(error);
      }

      if (origRequest.url === refreshUrl) {
        // refresh token call failed, so redirect to login
        if (onRefreshFailed) {
          onRefreshFailed();
          await sleep(100);
        }
        return Promise.reject(error);
      }

      if (error.response.status !== 401) {
        return Promise.reject(error);
      }

      accessTokenExpiresAt = 0;

      const originalRequest = toRetriableRequest(origRequest);

      if (originalRequest._retry) {
        return Promise.reject(error);
      }

      originalRequest._retry = true;
      const qPromise = queueRequest(_axios, originalRequest, true);
      if (isRefreshing) {
        // if we are not the first request to be queued, return a promise that will resolve when the queued request is executed
        return qPromise;
      }

      try {
        await updateToken(_axios, authTokenUpdated, fetchAccessToken);
      } catch (err) {
        if (onRefreshFailed) {
          onRefreshFailed();
          await sleep(100);
          return;
        } else {
          throw err;
        }
      }

      return qPromise;
    },
    request: async (origRequest: InternalAxiosRequestConfig) => {
      if (origRequest.url === refreshUrl) {
        return origRequest;
      }

      const originalRequest = toRetriableRequest(origRequest);

      if (!originalRequest._retry) {
        if (isRefreshing) {
          // we are already refreshing, so add to queue
          return queueRequest(_axios, originalRequest, false);
        }

        if (Date.now() < accessTokenExpiresAt) {
          return originalRequest;
        }

        try {
          const qPromise = queueRequest(_axios, originalRequest, false);
          await updateToken(_axios, authTokenUpdated, fetchAccessToken);
          // return the queued promise for the first request to trigger token refresh
          return qPromise;
        } catch (err) {
          if (onRefreshFailed) {
            onRefreshFailed();
            await sleep(100);
            return;
          } else {
            throw err;
          }
        }
      }
      return origRequest;
    },
  };
};

export default createInterceptor;
